import { TypedDocumentNode, WatchQueryFetchPolicy, gql } from '@apollo/client';

import { PaymentMethodProvider } from '__generated__/globalTypes';
import CreditCard from 'components/buyActions/CreditCard';
import useQuery from 'hooks/graphql/useQuery';
import useMangopayCreditCardsEnabled from 'hooks/useMangopayCreditCardsEnabled';

import {
  CurrentUserPaymentMethodsQuery,
  CurrentUserPaymentMethodsQueryVariables,
} from './__generated__/useCurrentUserPaymentMethods.graphql';

const CURRENT_USER_PAYMENT_METHODS_QUERY = gql`
  query CurrentUserPaymentMethodsQuery {
    currentUser {
      slug
      stripePaymentMethods: paymentMethods(provider: STRIPE) {
        id
        card {
          ...CreditCard_creditCard
        }
      }
      mangopayPaymentMethods: paymentMethods(provider: MANGOPAY) {
        id
        card {
          ...CreditCard_creditCard
        }
      }
      checkoutPaymentMethods: paymentMethods(provider: CHECKOUT) {
        id
        card {
          ...CreditCard_creditCard
        }
      }
    }
  }
  ${CreditCard.fragments.creditCard}
` as TypedDocumentNode<
  CurrentUserPaymentMethodsQuery,
  CurrentUserPaymentMethodsQueryVariables
>;

export const useCurrentUserPaymentMethods = (
  fetchPolicy?: WatchQueryFetchPolicy
) => {
  const useMangopayCreditCards = useMangopayCreditCardsEnabled();

  const { data } = useQuery(CURRENT_USER_PAYMENT_METHODS_QUERY, {
    fetchPolicy: fetchPolicy || 'cache-and-network',
  });

  if (!data?.currentUser) return [];

  const {
    currentUser: {
      stripePaymentMethods,
      mangopayPaymentMethods,
      checkoutPaymentMethods,
    },
  } = data;

  return [
    ...stripePaymentMethods.map(m => ({
      provider: PaymentMethodProvider.STRIPE,
      disabled: useMangopayCreditCards,
      ...m,
    })),
    ...mangopayPaymentMethods.map(m => ({
      provider: PaymentMethodProvider.MANGOPAY,
      disabled: false,
      ...m,
    })),
    ...checkoutPaymentMethods.map(m => ({
      provider: PaymentMethodProvider.CHECKOUT,
      disabled: false,
      ...m,
    })),
  ];
};
